import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, TrendingUp, Download, Printer, DollarSign,
  Clock, FileText, Users, ChevronLeft, ChevronRight, RefreshCw, FileSpreadsheet
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { reportsAPI, branchesAPI, bookingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import GregorianDatePicker from '../../components/GregorianDatePicker';

const DailyIncomeReport = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [dailyData, setDailyData] = useState({
    date: '',
    totalIncome: 0,
    paidAmount: 0,
    unpaidAmount: 0,
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0,
    avgBookingValue: 0,
    hourlyData: [],
    bookingsList: []
  });
  const [weeklyComparison, setWeeklyComparison] = useState([]);

  useEffect(() => {
    // Set default to today
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);

    // Fetch branches
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchesAPI.getAll({ limit: 100 });
      // paginatedResponse returns: { data: { items, total, page, limit } }
      const data = response.data?.data?.items || response.data?.items || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchDailyData();
    }
  }, [selectedDate, selectedBranch]);

  const fetchDailyData = async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (selectedBranch) params.branch_id = selectedBranch;

      const response = await reportsAPI.getDailyIncomeReport(params);

      const data = response.data?.data;

      if (data) {
        // Backend getDailyReport returns flat object with snake_case:
        // { total_bookings, total_sales, total_discounts, net_revenue, cash, pos, bank_transfer }
        // May also have summary wrapper or weeklyComparison from different endpoint
        const summary = data.summary || data;

        setDailyData({
          date: data.date || selectedDate,
          totalIncome: summary.total_sales || summary.totalIncome || summary.net_revenue || 0,
          paidAmount: summary.paidAmount || summary.cash || 0,
          unpaidAmount: summary.unpaidAmount || 0,
          totalBookings: summary.total_bookings || summary.totalBookings || 0,
          completedBookings: summary.completedBookings || 0,
          pendingBookings: summary.pendingBookings || 0,
          avgBookingValue: summary.avgBookingValue || (summary.total_sales / (summary.total_bookings || 1)) || 0,
          hourlyData: data.hourlyData || [],
          bookingsList: []
        });

        // Weekly comparison may not be available
        if (data.weeklyComparison) {
          const weeklyComparisonWithDays = data.weeklyComparison.map(item => ({
            ...item,
            day: new Date(item.date).toLocaleDateString('ar-SA-u-ca-gregory', { weekday: 'short' })
          }));
          setWeeklyComparison(weeklyComparisonWithDays);
        }
      }

    } catch (error) {
      console.error('Error fetching daily data:', error);
      toast.error('حدث خطأ أثناء جلب بيانات التقرير اليومي');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const [exporting, setExporting] = useState(false);

  const handleExportToExcel = async () => {
    setExporting(true);
    const loadingToast = toast.loading('جاري تجهيز ملف الإكسل...');
    
    try {
      const params = {
        date_from: selectedDate,
        date_to: selectedDate
      };
      if (selectedBranch) params.branch_id = selectedBranch;

      const response = await bookingsAPI.export(params);
      const bookings = response.data?.data || [];

      if (bookings.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('لا توجد حجوزات في هذا اليوم');
        setExporting(false);
        return;
      }

      const statusMap = {
        'new': 'جديد',
        'pending': 'معلق',
        'sample_collected': 'تم التجميع',
        'in_progress': 'قيد المعالجة',
        'completed': 'مكتمل',
        'cancelled': 'ملغي'
      };

      const paymentStatusMap = {
        'unpaid': 'غير مدفوع',
        'partial': 'مدفوع جزئياً',
        'paid': 'مدفوع بالكامل'
      };

      const paymentMethodMap = {
        'cash': 'نقدي',
        'card': 'بطاقة',
        'cash_card': 'نقدي + بطاقة',
        'bank_transfer': 'تحويل بنكي',
        'insurance': 'تأمين'
      };

      const headers = [
        'رقم الحجز',
        'الباركود',
        'الوقت',
        'اسم العميل',
        'رقم الجوال',
        'الفرع',
        'التحاليل',
        'المبلغ الإجمالي',
        'الخصم',
        'الضريبة',
        'المبلغ الصافي',
        'المبلغ المدفوع',
        'المتبقي',
        'طريقة الدفع',
        'حالة الدفع',
        'حالة الحجز',
        'الموظف'
      ];

      const rows = bookings.map(booking => {
        const testsArr = booking.tests?.filter(t => t.lab_test_id) || [];
        const tests = testsArr.length > 0
          ? testsArr.map(t => t.lab_test_id?.name_ar || t.lab_test_id?.name_en || '-').join(' | ')
          : '-';

        const bookingDate = new Date(booking.booking_date);

        return [
          booking.booking_no || '-',
          booking.barcode_no || '-',
          bookingDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
          booking.customer_id?.full_name || '-',
          booking.customer_id?.phone || '-',
          booking.branch_id?.branch_name || '-',
          tests,
          (booking.total_amount || 0).toFixed(2),
          (booking.discount_amount || 0).toFixed(2),
          (booking.vat_amount || 0).toFixed(2),
          (booking.net_amount || 0).toFixed(2),
          (booking.paid_amount || 0).toFixed(2),
          ((booking.net_amount || 0) - (booking.paid_amount || 0)).toFixed(2),
          paymentMethodMap[booking.payment_method] || '-',
          paymentStatusMap[booking.payment_status] || '-',
          statusMap[booking.status] || '-',
          booking.created_by_user_id?.name || '-'
        ];
      });

      const totals = bookings.reduce((acc, b) => ({
        total: acc.total + (b.total_amount || 0),
        discount: acc.discount + (b.discount_amount || 0),
        vat: acc.vat + (b.vat_amount || 0),
        net: acc.net + (b.net_amount || 0),
        paid: acc.paid + (b.paid_amount || 0)
      }), { total: 0, discount: 0, vat: 0, net: 0, paid: 0 });

      const summaryRows = [
        [],
        ['', '', '', '', '', '', 'الإجماليات:', totals.total.toFixed(2), totals.discount.toFixed(2), totals.vat.toFixed(2), totals.net.toFixed(2), totals.paid.toFixed(2), (totals.net - totals.paid).toFixed(2)],
        [],
        ['═══════════════════════════════════════════════════════════════════════════════════'],
        [`تقرير الدخل اليومي - ${selectedDate}`],
        [`عدد الحجوزات: ${bookings.length}`],
        [`إجمالي الإيرادات: ${totals.net.toFixed(2)} ر.س`],
        [`إجمالي المدفوع: ${totals.paid.toFixed(2)} ر.س`],
        [`إجمالي المتبقي: ${(totals.net - totals.paid).toFixed(2)} ر.س`]
      ];

      const csvData = [headers, ...rows, ...summaryRows];

      const escapeCsvValue = (val) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csv = csvData.map(row => row.map(escapeCsvValue).join(',')).join('\n');
      
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `تقرير_الدخل_اليومي_${selectedDate}.csv`;
      link.click();

      toast.dismiss(loadingToast);
      toast.success(`تم تصدير ${bookings.length} حجز بنجاح`);
    } catch (error) {
      console.error('Export error:', error);
      toast.dismiss(loadingToast);
      toast.error('حدث خطأ أثناء تصدير البيانات');
    }
    setExporting(false);
  };

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <Calendar className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">تقرير الدخل اليومي</h1>
              <p className="text-slate-500 text-sm mt-1">
                {new Date(selectedDate).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
          <div className="flex gap-2 print:hidden">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePrint}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
          >
            <Printer className="w-4 h-4" strokeWidth={2.5} />
            <span>طباعة</span>
          </motion.button>
            <motion.button
              whileHover={{ scale: exporting ? 1 : 1.02 }}
              whileTap={{ scale: exporting ? 1 : 0.98 }}
              onClick={handleExportToExcel}
              disabled={exporting}
              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                exporting 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
              }`}
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  <span>جاري التصدير...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4" strokeWidth={2.5} />
                  <span>تصدير إكسل</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Date Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 print:hidden"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            {/* Branch Filter */}
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">الفرع</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white"
              >
                <option value="">جميع الفروع</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>{branch.branch_name}</option>
                ))}
              </select>
            </div>

            {/* Date Navigation */}
            <div className="md:col-span-8">
              <div className="flex items-end gap-3">
              <button
                onClick={handlePreviousDay}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors border border-gray-200"
              >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex-1">
                  <GregorianDatePicker
                  value={selectedDate}
                    onChange={setSelectedDate}
                    maxDate={new Date().toISOString().split('T')[0]}
                />
              </div>
              <button
                onClick={handleToday}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                اليوم
              </button>
              <button
                onClick={handleNextDay}
                disabled={selectedDate >= new Date().toISOString().split('T')[0]}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100/80 rounded-xl backdrop-blur-sm">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">إجمالي الدخل اليومي</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{dailyData.totalIncome.toFixed(2)} <span className="text-base font-normal text-gray-400">ر.س</span></p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100/80 rounded-xl backdrop-blur-sm">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">عدد الحجوزات</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{dailyData.totalBookings}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100/80 rounded-xl backdrop-blur-sm">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">متوسط قيمة الحجز</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{dailyData.avgBookingValue.toFixed(2)} <span className="text-base font-normal text-gray-400">ر.س</span></p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100/80 rounded-xl backdrop-blur-sm">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">الحجوزات المعلقة</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{dailyData.pendingBookings}</p>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Hourly Breakdown */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">توزيع الدخل حسب الساعة</h3>
            {dailyData.hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailyData.hourlyData}>
                  <defs>
                    <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="hour" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'income') return [`${value.toFixed(2)} ر.س`, 'الدخل'];
                      if (name === 'count') return [value, 'العدد'];
                      return value;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#colorIncome)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>لا توجد حجوزات لهذا اليوم</p>
              </div>
            )}
          </motion.div>

          {/* Weekly Comparison */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">مقارنة آخر 7 أيام</h3>
            {weeklyComparison.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={weeklyComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'income') return [`${value.toFixed(2)} ر.س`, 'الدخل'];
                      if (name === 'count') return [value, 'الحجوزات'];
                      return value;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="income"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>لا توجد بيانات كافية</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Payment Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-6">حالة المدفوعات</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="p-3 bg-blue-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">المبلغ المدفوع</p>
                <p className="text-xl font-bold text-blue-600">{dailyData.paidAmount.toFixed(2)} ر.س</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="p-3 bg-gray-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">المبلغ غير المدفوع</p>
                <p className="text-xl font-bold text-gray-600">{dailyData.unpaidAmount.toFixed(2)} ر.س</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">نسبة التحصيل</p>
                <p className="text-xl font-bold text-blue-600">
                  {dailyData.totalIncome > 0 ? ((dailyData.paidAmount / dailyData.totalIncome) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Print Styles */}
        <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default DailyIncomeReport;

